import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { Button } from '../components';
import { useSession } from '../auth/session';
import { humanise } from '../format';
import { NavIcon } from './icons';
import { applyTheme, resolvedTheme, storedTheme, type Theme } from '../theme';

/**
 * The frame every signed-in screen renders inside.
 *
 * Navigation is filtered by permission — a reviewer does not see a link to user
 * management. This is presentation only: the server checks every request regardless, and
 * a hidden link is a courtesy rather than a control. SMA hid menu items and treated that
 * as the authorisation model, which is why any signed-in user could reach any endpoint by
 * typing the URL (S-2).
 */

interface NavEntry {
  to: string;
  label: string;
  permission?: string;
}

interface NavGroup {
  title: string;
  entries: NavEntry[];
}

/**
 * Screens with no navigation link of their own.
 *
 * Only one: `/change-password`, reached from the header rather than the sidebar. Anything
 * else appearing here is a screen somebody can reach and nobody can find, which is worth
 * noticing rather than naming.
 */
const OFF_NAV_TITLES: Record<string, string> = {
  '/change-password': 'Change password',
};

export function Shell(): JSX.Element {
  const [theme, setTheme] = useState<Theme>(() => storedTheme());
  const { user, config, signOut, can } = useSession();
  const { pathname } = useLocation();

  const groups: NavGroup[] = [
    {
      title: 'Overview',
      entries: [
        { to: '/', label: 'Dashboard' },
        { to: '/reports', label: 'Reports', permission: 'report.view' },
      ],
    },
    {
      title: 'Work',
      entries: [
        { to: '/submissions', label: 'Submissions', permission: 'submission.view.own' },
        { to: '/escalations', label: 'Escalations', permission: 'exception.view' },
        { to: '/schedule', label: 'Scheduling', permission: 'assignment.reschedule' },
      ],
    },
    {
      title: config.siteLabel + 's',
      entries: [
        { to: '/sites', label: `All ${config.siteLabel.toLowerCase()}s`, permission: 'site.view' },
        { to: '/tags', label: 'Tags', permission: 'site.view' },
        {
          to: '/location-requests',
          label: 'Location corrections',
          permission: 'location_request.decide',
        },
        { to: '/org-units', label: 'Organisation', permission: 'site.view' },
      ],
    },
    {
      title: 'Configuration',
      entries: [
        { to: '/forms', label: 'Forms & questions', permission: 'workflow.view' },
        { to: '/library', label: 'Question library', permission: 'workflow.view' },
        { to: '/rules', label: 'Rules', permission: 'rule.edit' },
        { to: '/visit-rules', label: 'Visit rules', permission: 'policy.edit' },
        { to: '/users', label: `${config.fieldUserLabel}s`, permission: 'user.view' },
        { to: '/roles', label: 'Roles', permission: 'user.view' },
        { to: '/webhooks', label: 'Integrations', permission: 'policy.edit' },
      ],
    },
    {
      title: 'Assurance',
      entries: [{ to: '/audit', label: 'Audit trail', permission: 'audit.view' }],
    },
  ];

  /**
   * The current page's name, taken from the navigation rather than kept in a second list.
   *
   * Two lists of route titles drift, and the one nobody looks at is the one that goes
   * stale. `/change-password` is not in the navigation, so it is named here.
   */
  const pageTitle =
    groups
      .flatMap((group) => group.entries)
      .find((entry) => (entry.to === '/' ? pathname === '/' : pathname.startsWith(entry.to)))
      ?.label ??
    OFF_NAV_TITLES[pathname] ??
    // Every real route is either in the navigation or named above, so anything left is the
    // catch-all. Falling back to the client's name here put the brand back in the one place
    // it says least — over a page that does not exist.
    'Not found';

  return (
    <div className="shell">
      <nav className="sidebar">
        <div className="sidebar-brand">
          <strong>{config.name}</strong>
          <span>Audit &amp; inspection</span>
        </div>

        <div className="sidebar-nav">
          {groups.map((group) => {
            const visible = group.entries.filter(
              (entry) => entry.permission === undefined || can(entry.permission),
            );
            // A group whose every entry is hidden should not leave a heading behind.
            if (visible.length === 0) return null;

            return (
              <div key={group.title}>
                <div className="sidebar-group">{group.title}</div>
                {visible.map((entry) => (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    end={entry.to === '/'}
                    className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
                  >
                    <NavIcon route={entry.to} />
                    <span>{entry.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </div>

        <div className="sidebar-footer">
          <div>{user?.fullName}</div>
          {/* The role's key was printed as stored: "store_manager". */}
          <div className="muted">{humanise(user?.role)}</div>
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          {/*
            Where you are, not who you are with. This said the client's name — the same
            words already at the top of the sidebar two inches to the left — which spent the
            one persistent line on screen repeating something and left the page itself
            unlabelled. Every screen draws its own card heading, but only after it loads.
          */}
          <h1>{pageTitle}</h1>
          <div className="row">
            {/*
              Cycles light → dark → system rather than toggling two. "System" has to be
              reachable: somebody whose machine switches at dusk should be able to hand the
              decision back after trying the other two, and a two-way switch traps them.
              The label says which is in force, so "System" is never ambiguous.
            */}
            <button
              className="btn btn-secondary theme-toggle"
              onClick={() => {
                const next: Theme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
                setTheme(next);
                applyTheme(next);
              }}
              title={`Appearance: ${theme}${theme === 'system' ? ` (${resolvedTheme(theme)})` : ''}. Click to change.`}
              aria-label={`Appearance: ${theme}. Click to change.`}
            >
              <span aria-hidden="true">{resolvedTheme(theme) === 'dark' ? '◐' : '◑'}</span>
              <span className="theme-toggle-label">
                {theme === 'system' ? 'System' : theme === 'dark' ? 'Dark' : 'Light'}
              </span>
            </button>
            <NavLink to="/change-password" className="muted">
              Change password
            </NavLink>
            <Button variant="secondary" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </header>

        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
